/**
 * Sayfa başlığı — **vücut** mertebesinin ilk parçası.
 *
 * CLAUDE.md "Ekran şablonları sabit" kuralı: her liste ekranı
 * `PageHeader → FilterBar → DataTable → Pagination` sırasıyla dizilir.
 * Bu bileşen o şablonun ilk halkası; her ekran kendi başlık düzenini
 * uydurmasın diye tek yerde.
 *
 * Üç parça, hep aynı yerde:
 *
 *   Ana ekran / Faturalar / DRC2026000000001     ← kırıntı (nerede olduğun)
 *   Fatura detayı                    [ Eylemler ] ← başlık + birincil eylem
 *   e-Arşiv · Satış · 03.09.2026                  ← bağlam
 *
 * **Kırıntı neden var:** detay ekranından listeye dönmek için tarayıcı geri
 * düğmesine bağımlı kalınıyordu. Kırıntı hem konumu söyler hem geri yolu
 * verir — Vuexy'nin her sayfada bulundurmasının sebebi bu.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';

export interface KirintiOgesi {
  etiket: string;
  /** Verilmezse bağlantı değil, düz metin olur (son öğe). */
  yol?: string;
}

export interface SayfaBasligiProps {
  baslik: string;
  /** Başlığın altındaki tek satır bağlam. */
  altBaslik?: string | undefined;
  /** Nerede olduğunu gösteren yol. Son öğe daima düz metin. */
  kirintilar?: KirintiOgesi[];
  /** Sağa yaslanan eylemler — birincil eylem en sağda. */
  eylemler?: ReactNode;
}

export function SayfaBasligi({ baslik, altBaslik, kirintilar, eylemler }: SayfaBasligiProps) {
  return (
    <div className="flex flex-col gap-1">
      {kirintilar && kirintilar.length > 0 && (
        <nav aria-label="Sayfa yolu" className="flex flex-wrap items-center gap-1 text-xs">
          {kirintilar.map((oge, sira) => {
            const sonuncu = sira === kirintilar.length - 1;
            return (
              <span key={`${oge.etiket}-${sira}`} className="flex items-center gap-1">
                {sira > 0 && (
                  <ChevronRight size={12} className="shrink-0 text-[var(--ink-faint)]" aria-hidden="true" />
                )}
                {oge.yol && !sonuncu ? (
                  <Link
                    to={oge.yol}
                    className="text-[var(--ink-muted)] hover:text-[var(--accent)] hover:underline"
                  >
                    {oge.etiket}
                  </Link>
                ) : (
                  // Son öğe bağlantı DEĞİL: bulunduğun sayfaya link vermek
                  // kullanıcıyı "bir şey olacak" diye yanıltır.
                  <span className="truncate text-[var(--ink-faint)]" aria-current="page">
                    {oge.etiket}
                  </span>
                )}
              </span>
            );
          })}
        </nav>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h1 className="truncate text-lg font-semibold text-[var(--ink)] lg:text-xl">{baslik}</h1>
          {altBaslik && <p className="truncate text-sm text-[var(--ink-muted)]">{altBaslik}</p>}
        </div>
        {eylemler && <div className="flex flex-wrap items-center gap-2">{eylemler}</div>}
      </div>
    </div>
  );
}
